import { AppBar, List, ListItem, ListItemIcon, ListItemText, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import FullScreenDialog from '../full-screen-dialog/FullScreenDialog';
import SettingsBar, { SettingsActionEnum } from '../settings-bar/SettingsBar';
import UnsavedChangesPrompt from '../unsaved-changes-prompt/UnsavedChangesPrompt';
import AccountSettingsHeader from './account-settings-header/AccountSettingsHeader';
import AccountProfile from './account-profile/AccountProfile';
import AccountPreferences from './account-preferences/AccountPreferences';
import Form from '../forms/form/Form';
import { useQueryNotifications } from '../../queries/UseQueryNotifications';
import { useUserSettings } from '../../queries/user-settings/UseUserSettings';
import { useSaveUserSettings } from '../../queries/user-settings/UseSaveUserSettings';
import { useOfflineConfig } from '../../queries/offline-config/UseOfflineConfig';
import { useSaveOfflineConfig } from '../../queries/offline-config/UseSaveOfflineConfig';
import AccountSettingUtils, { AccountSettingsFormType, defaultAccountSettings } from './AccountSettingUtils';
import { useForm } from 'react-hook-form';
import { UserStateActionTypeEnum, useUserContext } from '../../contexts/user-context/UserContext';
import { useSaveUserProfile } from '../../queries/user-settings/UseSaveUserProfile';
import { IeLynxUser } from '../../common/models/eLynxUser';
import { IUserSettings } from '../../services/user-settings-data/Models';
import { OfflineConfigSettings } from '../../services/offline-data-service/OfflineDataService';
import { AppSettingsActionTypeEnum, useAppSettings } from '../../contexts/app-context/AppContext';
import ApiManagement from './api-management/ApiManagement';
import { useUserRecipients } from '../../queries/user-role-administration/UseUserRecipients';
import { useRouteContext } from '../../hooks/use-route-context/UseRouteContext';
import { IRecipient } from '../../common/models/IRecipient';
import { useUserDefaultViews } from '../../queries/user-default-view/UseUserDefaultViews';
import { UIViewEnum } from '../../common/enums/UIViewEnum';
import { eLynxObjectTypeEnum } from '../../common/enums/eLynxObjectType';
import { useSaveUserDefaultView } from '../../queries/user-default-view/UseSaveUserDefaultView';
import { RecipientTypeEnum } from '../../common/enums/RecipientTypeEnum';
import { Phone, Sms } from '@mui/icons-material';

export type AccountSettingProps = {
    open: boolean;
    onClose: () => void
};

export default function AccountSettings({open, onClose}: AccountSettingProps) {
    
    const { t } = useTranslation();
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(0);
    const [offlineState] = useOfflineDetectContext();
    const { confirm, acknowledge } = useConfirmation();
    const [appSettings, dispatchAppSettings] = useAppSettings();
    const routeContext = useRouteContext();
    const [userState, dispatchUserState] = useUserContext();
    const { data: userSettings } = useQueryNotifications(useUserSettings)({
        enabled: !offlineState.offline
    });
    const { mutate: saveUserSettings } = useQueryNotifications(useSaveUserSettings)({});
    const { data: userRecipients, refetch: refetchRecipients } = useQueryNotifications(useUserRecipients)({
        groupID: routeContext.groupID as number,
        userID: userState.user?.id as number,
        enabled: !offlineState.offline && open
    });
    const { data: offlineConfig } = useQueryNotifications(useOfflineConfig)({});


    const { data: defaultViews } = useQueryNotifications(useUserDefaultViews)({
        enabled: !offlineState.offline
    });


    const { mutate: saveOfflineConfig } = useQueryNotifications(useSaveOfflineConfig)({});

    const { mutate: saveUserProfile } = useQueryNotifications(useSaveUserProfile)({
        onSuccess: (data) => {
            // Now that we've got the finalized profile data back, update
            // the state accordingly. This profile may contain additional
            // information beyond what we sent. Specifically, this may happen
            // with regard to some of the MFA information.
            dispatchUserState({
                type: UserStateActionTypeEnum.UserChanged,
                user: {...data,
                    baseGroup: userState.user?.baseGroup
                }
            });

            refetchRecipients();

            reset({...defaultAccountSettings}, {
                keepDirty: false, 
                keepDirtyValues: false
            });

        }
    });


    const { mutate: saveUserDefaultView } = useQueryNotifications(useSaveUserDefaultView)({});


    const form = useForm<AccountSettingsFormType>({
        //mode: "all",
        mode: 'onChange',
        defaultValues: {...defaultAccountSettings
        }
    });

    const {
        getValues,
        //watch,
        //trigger,
        formState: {
            dirtyFields,
            isDirty,
            isValid,
            isSubmitting
            //defaultValues
            //touchedFields
        },
        reset,
        //setValue,
        //control,
        //handleSubmit,
    } = form;

    const trimPhoneNumber = (value: string) => value.replace('+1', '').replace(/[-()+]/g, '').trim();

    const onCloseSettings = async (action: SettingsActionEnum): Promise<void> => {

        if (action === SettingsActionEnum.OK) {

            const formValues = getValues();

            if (AccountSettingUtils.hasProfileChanges(dirtyFields)) {


                const phoneNumber = formValues.phone.replace(/-/g, '')?.trim();                

                // Check if the phone number has changed and if so, check if it's used for any alarm notification recipients
                // If it is, then prompt the user to update the recipients
                if (phoneNumber !== userState.user!.phoneNumber) {

                    const matchingRecipients = (userRecipients ?? []).filter(o => o.recipientType !== RecipientTypeEnum.Email && trimPhoneNumber(o.to) === trimPhoneNumber(userState.user!.phoneNumber ?? ''));

                    if (matchingRecipients.length > 0) {
        
                        const result = await confirm({
                            dialogTitle: t("userEditor.steps.general.updateRecipients.title"),
                            yesButtonText: t("userEditor.steps.general.updateRecipients.yesButtonText"),
                            noButtonText: t("userEditor.steps.general.updateRecipients.noButtonText"),
                            showConfirmationText: false,
                            confirmationBodyComponent: 
                            <Stack direction='column' spacing={2}>
                                <Typography variant="body2">
                                    {t("userEditor.steps.general.updateRecipients.confirmationText1")}
                                </Typography>
                                <List dense={true}>
                                    {matchingRecipients.map((o, ndx) => (
                                    <ListItem key={ndx}>
                                        <ListItemIcon sx={{minWidth: 'auto', marginRight: theme.spacing(1)}}>
                                            {o.recipientType === RecipientTypeEnum.Voice ? <Phone /> : <Sms />}
                                        </ListItemIcon>
                                        <ListItemText primary={o.to} />
                                    </ListItem>
                                    ))}
                                </List>
        
                                <Typography variant="body2">
                                    {t("userEditor.steps.general.updateRecipients.confirmationText2")}
                                </Typography>
        
                              </Stack>,
                            showYesButton: true,
                            showNoButton: true,
                            showCloseButton: true
                        });
        
                        if (result === 'close') {
                            return;
                        }
        
                        if (result === 'yes') {
        
                            const newRecipients = [...formValues.recipients];
        
                            matchingRecipients.forEach(m => {
                                const index = newRecipients.findIndex(o => o.to === m.to);
                                if (index !== -1) {
                                    const recipient = newRecipients[index];
                                    const parts = recipient.to.split(' ');
                                    recipient.to = `${parts[0]} ${phoneNumber}`;
        
                                    newRecipients[index] = recipient
                                }
                            });
        
                            formValues.recipients = newRecipients;
                        }
                    }
                }

                const model = AccountSettingUtils.mapToProfileModel(formValues, userState.user as IeLynxUser, userRecipients as IRecipient[]);
                saveUserProfile(model);
            }

            if (AccountSettingUtils.hasPreferenceChanges(dirtyFields)) {

                const model = AccountSettingUtils.mapToPreferencesModel(formValues, userSettings as IUserSettings);

                saveUserSettings(model);

                saveOfflineConfig({
                    ...offlineConfig,
                    enabled: formValues.offlineMode ?? false
                } as OfflineConfigSettings);


                if (formValues.theme !== appSettings.themeSet.mode.toString()) {
                    dispatchAppSettings({ 
                        type: AppSettingsActionTypeEnum.ThemeSetMode, 
                        mode: formValues.theme === 'light' ? 
                            'light' : 
                            'dark' 
                    });
                }

                if (dirtyFields?.defaultDeviceView) {

                    const currentDefaultView = defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Device &&
                                                    o.user?.id === userState.user?.id);

                    saveUserDefaultView({
                        id: currentDefaultView?.id ?? -1,
                        user: userState.user,
                        objectID: undefined,
                        objectType: eLynxObjectTypeEnum.Device,
                        viewID: formValues.defaultDeviceView
                    });
                }

                if (dirtyFields?.defaultGroupView) {

                    const currentDefaultView = defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group &&
                                                    o.user?.id === userState.user?.id);

                    saveUserDefaultView({
                        id: currentDefaultView?.id ?? -1,
                        user: userState.user,
                        objectID: undefined,
                        objectType: eLynxObjectTypeEnum.Group,
                        viewID: formValues.defaultGroupView
                    });
                }


                dispatchUserState({
                    type: UserStateActionTypeEnum.UserState,
                    userState: {
                        ...userState,
                        userSettings: model as IUserSettings
                    }
                });

            }

            acknowledge(t('profile.accountSettingsUpdated'));

            reset({...defaultAccountSettings}, {
                keepDirty: false, 
                keepDirtyValues: false
            });


        } else if (isDirty) {
            const result = await getUserConfirmation();
            if (!result)  {
                return;
            }
        }

        onClose();        
    }

    const getUserConfirmation = async () => {

        return (await confirm({
            dialogTitle: t('cancelConfirmation.title'),
            confirmationText: t('profile.cancelEditText'),
            yesButtonText: t('cancelConfirmation.yesButtonText'),
            noButtonText: t('cancelConfirmation.noButtonText'),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    };


    useEffect(() => {

        if (open) {

            let formData: AccountSettingsFormType | undefined = undefined;

            if (offlineState.offline && userState.user && offlineConfig) {

                formData = AccountSettingUtils.mapToForm(userState.user,
                    undefined,
                    undefined,
                    offlineConfig,
                    UIViewEnum.Device_Details,
                    UIViewEnum.Group_SummaryListing
                );

            } else if (userState.user && userSettings && offlineConfig && userRecipients) {

                const defaultDeviceView = defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Device && 
                                                o.user?.id === userState.user?.id) ??
                                            defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Device && 
                                                    !o.user);

                const defaultGroupView = defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && 
                                            o.user?.id === userState.user?.id) ??
                                        defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && 
                                            !o.user);
    

                formData = AccountSettingUtils.mapToForm(userState.user,
                    userSettings,
                    userRecipients,
                    offlineConfig,
                    defaultDeviceView?.viewID ?? UIViewEnum.Device_Details,
                    defaultGroupView?.viewID ?? UIViewEnum.Group_SummaryListing);
            }

            if (formData) {
                reset(formData, {
                    keepDirty: true, 
                    keepDirtyValues: true
                });
            }
        }
    }, [
        userState.user,
        userSettings,
        userRecipients,
        reset,
        offlineConfig,
        offlineState.offline,
        open,
        defaultViews
    ]);

    useEffect(() => {

        if (open) {

            setActiveTab(0);
        }

    }, [
        open,
        //setActiveTab
    ])

    return (

        <FullScreenDialog open={open} onClose={onClose}>

                <SettingsBar title={t('profile.account')} 
                    onClose={onCloseSettings}
                    modified={isDirty && isValid  && !isSubmitting} />

                <AccountSettingsHeader />

                <AppBar position='static' color='default' style={{marginTop: '16px'}}>
                    <Tabs value={activeTab}
                            centered={true}
                            aria-label="profile and settings"
                            indicatorColor="secondary"
                            textColor="inherit"
                            onChange={(_event, value) => setActiveTab(value)}>

                        <Tab label={t('profile.profile')} 
                            style={{
                                //flexBasis: '50%'
                            }} 
                        />

                        {
                            !offlineState.offline && 

                                <Tab label={t('profile.settings')} 
                                    style={{
                                        //flexBasis: '50%'
                                    }} 
                                />

                        }

                        {
                            !offlineState.offline && 
                        
                                <Tab label={t('profile.apiManagement')} 
                                    style={{
                                        //flexBasis: '50%'
                                    }} 
                                />
                        }                        
                    </Tabs>
                </AppBar>

                <Form form={form}>

                    <AccountProfile visible={activeTab === 0}  />

                    {
                        !offlineState.offline &&
                        <>
                            <AccountPreferences
                                visible={activeTab === 1} 
                            />

                            <ApiManagement
                                visible={activeTab === 2} 
                            
                            />
                        </>

                    }


                </Form>



                <UnsavedChangesPrompt
                    hasChanges={isDirty}
                    getUserConfirmation={getUserConfirmation}
                />


        </FullScreenDialog>
        
    );
  }
